// Full-document render of the Oscar account-profile (display) view:
// PageSkeleton wrapping the tempurpedic account landing -- the "Your
// Account" welcome header (its per-brand copy resolved server-side via the
// profile_welcome_html seam), the Your Orders / Your Addresses cards, the
// account-information list with the password-management links, and the
// customer-service info block (TP's divergent island, via the cs_info_json
// seam). Visible copy uses ttag `t` so tempurca (fr_CA) stays localized.
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { templates } from "@reactivated";

import { AuthCustomerServiceBlock } from "../components/AuthCustomerServiceBlock";
import { PageSkeleton } from "../components/PageSkeleton";

export const Template = (
    props: templates.ProfileDocumentTemplate,
): React.JSX.Element => {
    const staticURL = useStaticURL();
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {/* {% block extra_meta %}: React 19 hoists this into <head>. */}
            <meta name="chatTags" content="profile" />
            <section className="account-profile">
                <div className="l-capped-width">
                    <div className="account-profile__intro">
                        <h2>{t`Your Account`}</h2>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: props.profile_welcome_html,
                            }}
                        />
                    </div>

                    <div className="account-profile__card">
                        <img
                            src={`${staticURL}img/icons/badge-orders.png`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                        <h3>{t`Your Orders`}</h3>
                        <p>{t`Track, review, and update order status`}</p>
                        <a href={props.order_list_url}>{t`View details`}</a>
                    </div>

                    <div className="account-profile__card">
                        <img
                            src={`${staticURL}img/icons/badge-addresses.png`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                        <h3>{t`Your Addresses`}</h3>
                        <p>{t`View and edit your saved addresses`}</p>
                        <a href={props.address_list_url}>{t`View details`}</a>
                    </div>

                    <div className="account-profile__info">
                        {/* Not translated. */}
                        <h2>Account Information</h2>
                        <dl>
                            <div>
                                <dt>{t`Name`}</dt> <dd>{props.full_name}</dd>
                            </div>
                            <div>
                                <dt>{t`Email`}</dt> <dd>{props.email}</dd>
                            </div>
                            <div>
                                <dt>{t`Date Registered`}</dt>{" "}
                                <dd>{props.date_registered_short}</dd>
                            </div>
                        </dl>

                        {props.is_sso ? (
                            <p>{props.sso_message}</p>
                        ) : (
                            <>
                                <a
                                    className="link"
                                    href={props.profile_update_url}
                                >
                                    {t`Edit Profile`}
                                </a>{" "}
                                |{" "}
                                {props.has_password ? (
                                    <a
                                        className="link"
                                        href={props.change_password_url}
                                    >
                                        {t`Change Password`}
                                    </a>
                                ) : (
                                    <a
                                        className="link"
                                        href={props.register_url}
                                    >
                                        {t`Create Password`}
                                    </a>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>

            <AuthCustomerServiceBlock payload={props.cs_info_json} />
        </PageSkeleton>
    );
};
